import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { PieChart as MuiPieChart } from "@mui/x-charts/PieChart";
import { DashboardWidget } from "./DashboardWidget";
import { brand, gray } from "../../../../theme/themePrimitives";
import { useCustomisation } from "../../../../context/CustomisationContext";
import { hasSomeCustomisation } from "../../../../utils/helpers";
import { usePortal } from "../../../../context/PortalsContext";

export interface AssignmentsWidgetProps {
  data: Array<{
    id: string | number;
    value: number;
    label: string;
  }>;
  total?: number;
  withoutWrapper?: boolean;
  title?: string;
  legendTitle?: string;
  wrapperTitle?: string;
  centerContent?: boolean;
}

export const AssignmentsWidget: React.FC<AssignmentsWidgetProps> = ({
  data,
  total,
  withoutWrapper = false,
  title = "Active assignments",
  legendTitle = "Active assignments",
  wrapperTitle = "Assignments",
  centerContent = false,
}) => {
  const theme = useTheme();
  const { customisations, updateCustomisations } = useCustomisation();
  const { portals } = usePortal();
  const {
    HOME: { assignmentStatusColorsSettings, textColorsSettings },
    GPC: { textColorsSettings: gpcTextColorsSettings },
  } = customisations.customisations;

  const isDashboardRoute = window.location.pathname.includes("dashboard");

  const secondaryTextColor =
    textColorsSettings?.homeSecondaryTextColor ||
    gpcTextColorsSettings?.gpcSecondaryTextColor;

  const calculatedTotal =
    total || data.reduce((sum, item) => sum + item.value, 0);
  const hasZeroValues = calculatedTotal === 0;
  const colors = [
    assignmentStatusColorsSettings?.homeCompleted || brand[600],
    assignmentStatusColorsSettings?.homePending || theme.palette.warning.main,
    assignmentStatusColorsSettings?.homeOverdue || theme.palette.error.main,
  ];

  const chartData = hasZeroValues
    ? [
        {
          id: "empty",
          value: 100,
          label: "",
          color: theme.palette.action.disabled,
        },
      ]
    : data.map((item, index) => ({
        ...item,
        color: colors[index % colors.length],
      }));

  const dataWithPercentages = data.map((item, index) => ({
    ...item,
    percentage: hasZeroValues
      ? "0.0"
      : ((item.value / calculatedTotal) * 100).toFixed(1),
    color: colors[index % colors.length],
  }));

  const containerRef = useRef<HTMLDivElement>(null);
  const [isWrapped, setIsWrapped] = useState(false);

  // On touch devices, MUI's hover tooltip can't distinguish a tap from a
  // scroll-drag, causing it to flicker, and its Popper is anchored to the
  // pointer so it can render on top of the page header. Just turn it off
  // for touch — the legend below the chart already shows every value.
  const isCoarsePointer = useMediaQuery("(pointer: coarse)");
  const isTouchDevice =
    isCoarsePointer ||
    (typeof navigator !== "undefined" && navigator.maxTouchPoints > 0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const checkWrap = () => {
      const children = container.children;
      if (children.length < 2) return;

      const firstRect = children[0].getBoundingClientRect();
      const secondRect = children[1].getBoundingClientRect();
      // Second child below first = content has wrapped
      setIsWrapped(secondRect.top > firstRect.bottom + 2);
    };

    checkWrap();
    const observer = new ResizeObserver(checkWrap);
    observer.observe(container);
    return () => observer.disconnect();
  }, [data, total]);

  useEffect(() => {
    const fetchCustomisation = async () => {
      if (
        isDashboardRoute &&
        !hasSomeCustomisation(customisations.customisations, "HOME")
      ) {
        await updateCustomisations("HOME");
      }
    };
    fetchCustomisation();
  }, [
    customisations.customisations,
    isDashboardRoute,
    updateCustomisations,
    portals.selectedPortal,
  ]);

  const content = (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: isWrapped
          ? "center"
          : centerContent
            ? "center"
            : undefined,
        gap: 1,
        minWidth: 0,
        maxWidth: "100%",
        width: "100%",
      }}
    >
      <Box sx={{ flex: "0 0 auto", position: "relative" }}>
        <Box
          sx={{
            opacity: hasZeroValues ? 0.5 : 1,
            pointerEvents: hasZeroValues ? "none" : "auto",
          }}
        >
          <MuiPieChart
            hideLegend
            series={[
              {
                data: chartData,
                innerRadius: 60,
                outerRadius: 80,
              },
            ]}
            width={185}
            height={185}
            slotProps={{
              tooltip: { trigger: isTouchDevice ? "none" : "item" },
            }}
          />
        </Box>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 600,
              color: isDashboardRoute ? gray[600] : secondaryTextColor,
            }}
          >
            {calculatedTotal}
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: isDashboardRoute ? gray[600] : secondaryTextColor,
            }}
          >
            {title}
          </Typography>
        </Box>
      </Box>
      {withoutWrapper ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 16,
              color: isDashboardRoute ? gray[600] : secondaryTextColor,
            }}
          >
            {legendTitle}
          </Typography>
          {dataWithPercentages.map((item) => (
            <Box
              key={item.id}
              sx={{
                display: "grid",
                gridTemplateColumns: "8px 55px 35px 1fr",
                alignItems: "center",
                columnGap: 1,
              }}
            >
              {/* dot */}
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: item.color,
                }}
              />

              {/* percentage */}
              <Typography
                sx={{
                  fontWeight: 600,
                  textAlign: "left",
                  fontVariantNumeric: "tabular-nums",
                  color: item.color,
                }}
              >
                {item.percentage}%
              </Typography>

              {/* value */}
              <Typography
                sx={{
                  textAlign: "left",
                  fontVariantNumeric: "tabular-nums",
                }}
                color={isDashboardRoute ? gray[600] : secondaryTextColor}
              >
                ({item.value})
              </Typography>

              {/* label */}
              <Typography
                variant="body2"
                color={isDashboardRoute ? gray[600] : secondaryTextColor}
                textAlign={"left"}
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
          }}
        >
          {dataWithPercentages.map((item) => (
            <Box
              key={item.id}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: item.color,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  color={isDashboardRoute ? gray[600] : secondaryTextColor}
                >
                  {item.label}
                </Typography>
              </Box>

              <Typography sx={{ fontVariantNumeric: "tabular-nums", ml: 2 }}>
                <Box
                  component="span"
                  sx={{ fontWeight: 600, color: item.color }}
                >
                  {item.value}
                </Box>{" "}
                <Box
                  component="span"
                  sx={{
                    color: isDashboardRoute ? gray[600] : secondaryTextColor,
                  }}
                >
                  ({item.percentage}%)
                </Box>
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );

  if (withoutWrapper) {
    return content;
  }

  return <DashboardWidget title={wrapperTitle}>{content}</DashboardWidget>;
};
